import { defineSemanticTokens } from '@chakra-ui/react'

export const colors = defineSemanticTokens.colors({
  /**
   * The brand ramp. Communo's yellow by default; a network's own colour when one is served.
   *
   * Every step is a CSS variable with the Communo shade as its fallback, so a page that sets
   * nothing renders exactly as it did before this token existed. A dedicated space overrides
   * the `--communo-brand-*` variables once, on `:root`, and the whole application follows —
   * including `colorPalette: 'brand'` in `globalCss`, which is what makes every Chakra recipe
   * (buttons, tabs, switches…) inherit the colour without naming it.
   *
   * The indirection is the point: this file states *where* the brand colour comes from, and
   * the server states *what* it is. Nothing in the components has to know either.
   */
  brand: {
    50: { value: 'var(--communo-brand-50, var(--chakra-colors-yellow-50))' },
    100: { value: 'var(--communo-brand-100, var(--chakra-colors-yellow-100))' },
    200: { value: 'var(--communo-brand-200, var(--chakra-colors-yellow-200))' },
    300: { value: 'var(--communo-brand-300, var(--chakra-colors-yellow-300))' },
    400: { value: 'var(--communo-brand-400, var(--chakra-colors-yellow-400))' },
    500: { value: 'var(--communo-brand-500, var(--chakra-colors-yellow-500))' },
    600: { value: 'var(--communo-brand-600, var(--chakra-colors-yellow-600))' },
    700: { value: 'var(--communo-brand-700, var(--chakra-colors-yellow-700))' },
    800: { value: 'var(--communo-brand-800, var(--chakra-colors-yellow-800))' },
    900: { value: 'var(--communo-brand-900, var(--chakra-colors-yellow-900))' },
    950: { value: 'var(--communo-brand-950, var(--chakra-colors-yellow-950))' },

    // The derived steps Chakra generates for every built-in palette but not for a custom one.
    // Without them `colorPalette: 'brand'` would leave buttons and focus rings unstyled.
    // Mirrors Chakra's own `yellow` mapping step for step, so switching the global palette
    // from `yellow` to `brand` changes nothing on a page that overrides no variable.
    // Derived from the brand colour rather than fixed, and the fallback is what Communo used to
    // get unconditionally. See `deriveOnBrand`: a yellow yields a brown, a near-black a pale
    // grey, always at 4.5:1 or better against `solid`. Black was correct only while `solid` was
    // a pale step — the moment it became the colour an administrator actually picked, a dark
    // brand would have written black on near-black.
    contrast: {
      value: {
        _light: 'var(--communo-brand-on-solid, black)',
        _dark: 'var(--communo-brand-on-solid, black)',
      },
    },
    fg: {
      value: {
        _light: 'var(--chakra-colors-brand-800)',
        _dark: 'var(--chakra-colors-brand-300)',
      },
    },
    subtle: {
      value: {
        _light: 'var(--chakra-colors-brand-100)',
        _dark: 'var(--chakra-colors-brand-900)',
      },
    },
    muted: {
      value: {
        _light: 'var(--chakra-colors-brand-200)',
        _dark: 'var(--chakra-colors-brand-800)',
      },
    },
    emphasized: {
      value: {
        _light: 'var(--chakra-colors-brand-300)',
        _dark: 'var(--chakra-colors-brand-700)',
      },
    },
    // The colour as it was chosen — not a step of the ramp, which is the one thing none of the
    // eleven is. Every step is derived at a fixed lightness, so step 300 turned a `#2563EB` into
    // `#cbddff`: a network picked a blue and got a button the colour of a hint. It only ever
    // looked right for Communo, whose own yellow the profile was measured from.
    //
    // This is why `contrast` above had to become derived too. The two are one decision: a
    // surface wearing the real colour needs a label computed for that colour, and the old
    // pairing (a pale step, black text) only held because the surface was always pale.
    solid: {
      value: {
        _light: 'var(--communo-brand-base, var(--chakra-colors-yellow-400))',
        _dark: 'var(--communo-brand-base, var(--chakra-colors-yellow-400))',
      },
    },
    focusRing: {
      value: {
        _light: 'var(--chakra-colors-brand-500)',
        _dark: 'var(--chakra-colors-brand-500)',
      },
    },
    // The boundary of an outline button, a bordered card, an input's focus edge — the things
    // WCAG 2.2 §1.4.11 asks 3:1 of. Step 500 gave 1.6:1 on white for every brand colour
    // including Communo's own, so every outline button in the product has been drawing a border
    // nobody can see. It is the same mistake as the pale text beside it: a step measured for a
    // *surface*, asked to do a job on the opposite canvas.
    //
    // Mode-dependent, unlike `solid` and `contrast`: a boundary has to separate itself from the
    // canvas behind it, and the canvas is what changes with the colour mode.
    border: {
      value: {
        _light: 'var(--chakra-colors-brand-700)',
        _dark: 'var(--chakra-colors-brand-400)',
      },
    },
  },
  bg: {
    // App shell / page canvas. Warm cream in light (design `C.page`) so the
    // whole authenticated app reads calm; unchanged bluish-gray shell in dark.
    // Single source of truth — consumed by the Layout wrapper, never a raw hex.
    canvas: {
      value: {
        _light: 'var(--chakra-colors-cream-50)',
        _dark: 'var(--chakra-colors-gray-700)',
      },
    },
    emphasized: {
      value: {
        _light: 'var(--chakra-colors-gray-300)',
        _dark: 'var(--chakra-colors-gray-700)',
      },
    },
    panel: {
      // White sections on the warm cream page (design-system card model:
      // white surfaces + 2px borders), instead of a cold gray that clashes
      // with cream. Dark stays a hair darker than the bluish-gray shell so
      // panels keep their separation.
      value: {
        _light: 'white',
        _dark: 'var(--chakra-colors-gray-800)',
      },
    },
  },
  border: {
    DEFAULT: {
      value: {
        _light: 'var(--chakra-colors-gray-300)',
        _dark: 'var(--chakra-colors-gray-600)',
      },
    },
  },
  // Login SSO surfaces. The mock's warm palette exists nowhere else in the design system, and
  // some of its values fail WCAG 2.2 on their own, so the light values below are the corrected
  // ones (see each note) and every hue gets a dark counterpart.
  sso: {
    notice: {
      // "Recognized public-sector address" banner.
      bg: { value: { _light: '#EFF6EF', _dark: '{colors.green.900}' } },
      border: { value: { _light: '#CDE3CD', _dark: '{colors.green.700}' } },
      fg: { value: { _light: '#2F6B3A', _dark: '{colors.green.200}' } },
    },
    pill: {
      // Social provider pills.
      bg: { value: { _light: 'white', _dark: '{colors.gray.800}' } },
      // Mock says #D9D3BE ≈ 1.6:1 on white, which fails WCAG 1.4.11 (3:1 required when the
      // border is a component's only boundary). Darkened to ≈3.1:1.
      border: { value: { _light: '#B8AF93', _dark: '{colors.gray.600}' } },
      hoverBg: { value: { _light: '#FBF7EA', _dark: '{colors.gray.700}' } },
    },
    divider: {
      line: { value: { _light: '#EAE3CC', _dark: '{colors.gray.600}' } },
      // Mock says #B9B29C ≈ 2.2:1 on the cream card, which fails WCAG 1.4.3 at 13px.
      // Darkened to ≈4.6:1.
      fg: { value: { _light: '#8A8371', _dark: '{colors.gray.400}' } },
    },
    custom: {
      // Network connector button: near-black in light, lifted slightly in dark so it does not
      // disappear into the canvas.
      bg: { value: { _light: '#33302A', _dark: '#3D3A33' } },
      hoverBg: { value: { _light: '#1F1D18', _dark: '#4A473E' } },
      activeBg: { value: { _light: '#000000', _dark: '#2B2924' } },
      accent: { value: { _light: '#F7D558', _dark: '#F7D558' } },
    },
    note: {
      fg: { value: { _light: '#8A8371', _dark: '{colors.gray.400}' } },
    },
    connect: {
      // DSFR "disabled grey" for the coming-soon FranceConnect button.
      disabledBg: { value: { _light: '#e5e5e5', _dark: '{colors.gray.700}' } },
      disabledFg: { value: { _light: '#929292', _dark: '{colors.gray.500}' } },
    },
    proConnect: {
      // State branding: identical in both modes, non-negotiable.
      bg: { value: { _light: '#000091', _dark: '#000091' } },
      hoverBg: { value: { _light: '#1212FF', _dark: '#1212FF' } },
      activeBg: { value: { _light: '#2323FF', _dark: '#2323FF' } },
      fg: { value: { _light: '#F5F5FE', _dark: '#F5F5FE' } },
      // #000091 text is unreadable on a dark canvas, so only the LINK colour adapts.
      link: { value: { _light: '#000091', _dark: '#8B8BFF' } },
    },
  },
})
